// The name the print dialog gives the PDF it makes: the PDF's own title, the file E-Docs saves and
// the one Email to Patient attaches.
//
// ENG-2277 (HTML Forms): this is the one place a digital Form's file is named, for every surface that
// opens one. E-Docs lists the PDF under this name, so a digital Form is filed under its own name, not
// the surface's: it need not be a purchase agreement, or a repair form, at all. Any other form, and
// every form with HTML_FORMS_FEATURE off, keeps the name its surface gives it, exactly as before.
import { format } from "date-fns";

import { isDigitalForm } from "components/form-templates-field/digital-forms.utils";
import type { FormTemplate } from "services/types/form-templates.type";

/** What a surface hands over about the file's name (see CustomPrintFormMetadata). */
type SurfaceNameParts = {
  fileNamePostfix?: string;
  fileNameRecord?: string;
};

type NamedPatient = { first_name?: string | null; last_name?: string | null };

/**
 * The longest name a digital Form's file is given. E-Docs (Box) takes 255 characters at most; this
 * stays under it with room to spare for the " (1)" the server adds to a second file of the same name.
 */
export const DIGITAL_FORM_FILE_NAME_MAX_LENGTH = 240;

const EXTENSION = ".pdf";

// The moment a name is made, written as the purchase agreement dialog writes the one in its own names.
const NAME_MOMENT_FORMAT = "yyyyMMddHHmmss";

// A name as part of a file name: what a file name cannot hold is left out (a slash would have the
// server keep only what follows it), and so is a ".pdf" the name itself ends in, as the file gets
// its own.
const toFileNamePart = (name: string) =>
  name
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/(\.pdf)+$/i, "")
    .trim();

// As much of the start of a text as fits in `room` characters. Whole characters only: one that takes
// two places (an emoji) is kept or left out, never halved.
const startOf = (text: string, room: number) => {
  let kept = "";
  for (const character of Array.from(text)) {
    if (kept.length + character.length > room) break;
    kept += character;
  }
  return kept.trim();
};

/**
 * The open digital Form's own name. Null for any other form, and for every form with
 * HTML_FORMS_FEATURE off: those keep the name their surface gives them, as before.
 */
export const digitalFormNameOf = (
  template: FormTemplate | null | undefined,
  htmlFormsEnabled: boolean
) =>
  htmlFormsEnabled && isDigitalForm(template) ? (template?.title ?? "") : null;

/**
 * What tells one file of a digital Form from the next for the same patient. The surface's own record
 * when it hands one over (the purchase agreement dialog: its invoice, the agreement's date and the
 * moment the name was made). On every other surface, the date and time the form was opened, so a
 * second save of the form is not a second file of the same name, which the server would rename "(1)".
 */
export const digitalFormRecordOf = (
  surfaceRecord: string | undefined,
  openedAt: Date | null
) => surfaceRecord || (openedAt ? format(openedAt, NAME_MOMENT_FORMAT) : "");

/**
 * A digital Form's file name: the patient, the form's own name, then its record. The patient's name
 * and the form's are both cleaned for a file name, and shortened when the whole would be longer than
 * a file's name may be: each keeps at least half the room they share, and the one that needs less
 * leaves the rest to the other. The record and ".pdf" are always whole.
 */
export const digitalFormFileName = ({
  patientName,
  formName,
  record
}: {
  patientName: string;
  formName: string;
  record: string;
}) => {
  // What the two names share: the whole, less ".pdf", the "_" after the patient, and the record with
  // the space before it.
  const room = Math.max(
    0,
    DIGITAL_FORM_FILE_NAME_MAX_LENGTH -
      EXTENSION.length -
      1 -
      (record ? record.length + 1 : 0)
  );
  const cleanFormName = toFileNamePart(formName);
  const patient = startOf(
    toFileNamePart(patientName),
    Math.max(Math.floor(room / 2), room - cleanFormName.length)
  );
  const form = startOf(cleanFormName, room - patient.length);
  const afterPatient = [form, record].filter(Boolean).join(" ");
  return `${patient}${afterPatient ? `_${afterPatient}` : ""}${EXTENSION}`;
};

/** The file name of the PDF the print dialog makes, for the form that is open in it. */
export const printedFormFileName = ({
  patient,
  digitalFormName,
  surface,
  openedAt
}: {
  patient: NamedPatient | undefined;
  /** From digitalFormNameOf: null for any form that is not a digital Form. */
  digitalFormName: string | null;
  surface: SurfaceNameParts;
  /** When the dialog was last opened. */
  openedAt: Date | null;
}) => {
  if (!patient) return "form.pdf";
  if (digitalFormName === null)
    return `${patient?.first_name} ${patient?.last_name}${surface.fileNamePostfix ? "_" + surface.fileNamePostfix : ""}.pdf`;
  return digitalFormFileName({
    patientName: `${patient.first_name} ${patient.last_name}`,
    formName: digitalFormName,
    record: digitalFormRecordOf(surface.fileNameRecord, openedAt)
  });
};
